import { Auth } from "aws-amplify";
import { AmplifyEventBus } from "aws-amplify-vue";
import store from "@/store";
import { apolloClient, onLogin, onLogout } from "@/vue-apollo.js";
import { GET_MEDICO_BY_COGNITO_ID } from "@/graphql/queries/medicos.js";
import { GET_PACIENTE_BY_COGNITO_ID } from "@/graphql/queries/pacientes.js";
import { ACEPTAR_CONDICIONES } from "@/graphql/mutations/pacientes.js";
import { Loading } from "quasar";

async function getUser(force = false) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user && user.signInUserSession) {
      await onLogin(apolloClient, user.signInUserSession.accessToken.jwtToken);
      if (force || !store.getters["auth/getUser"]) {
        // Check if medico or paciente
        if (user.attributes["custom:group"] === "medicos") {
          let internal = await apolloClient.query({
            query: GET_MEDICO_BY_COGNITO_ID,
            variables: {
              cognito_id: user.attributes.sub
            }
          });
          user.internal = internal.data.findMedicoBy;
          store.dispatch("auth/setUser", { user });
          return user;
        } else {
          let internal = await apolloClient.query({
            query: GET_PACIENTE_BY_COGNITO_ID,
            variables: {
              cognito_id: user.attributes.sub
            }
          });
          user.internal = internal.data.findPacienteBy;
          store.dispatch("auth/setUser", { user });
          return user;
        }
      } else {
        return store.getters["auth/getUser"];
      }
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}

function signUp(username, password) {
  return Auth.signUp({
    username,
    password,
    attributes: {
      email: username
    }
  })
    .then(data => {
      AmplifyEventBus.$emit("localUser", data.user);
      if (data.userConfirmed === false) {
        AmplifyEventBus.$emit("authState", "confirmSignUp");
      } else {
        AmplifyEventBus.$emit("authState", "signIn");
      }
      return data;
    })
    .catch(err => {
      console.log(err);
    });
}

function confirmSignUp(username, code) {
  return Auth.confirmSignUp(username, code)
    .then(data => {
      AmplifyEventBus.$emit("authState", "signIn");
      return data; // 'SUCCESS'
    })
    .catch(err => {
      console.log(err);
      throw err;
    });
}

function resendSignUp(username) {
  return Auth.resendSignUp(username)
    .then(() => {
      return "SUCCESS";
    })
    .catch(err => {
      console.log(err);
      return err;
    });
}

async function signIn(username, password) {
  try {
    const user = await Auth.signIn(username, password);
    if (user) {
      let realUser = await getUser();
      store.dispatch("auth/setUser", { user: realUser ? realUser : user });
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        AmplifyEventBus.$emit("authState", "resetPassword");
      } else {
        const usr = realUser ? realUser : user;
        await onLogin(
          apolloClient,
          usr.signInUserSession.accessToken.jwtToken
        ).then(() => {
          AmplifyEventBus.$emit("authState", "signedIn");
        });
      }
      return true;
    }
  } catch (err) {
    if (err.code === "UserNotConfirmedException") {
      // The error happens if the user didn't finish the confirmation step when signing up
      // In this case you need to resend the code and confirm the user
      // About how to resend the code and confirm the user, please check the signUp part
    } else if (err.code === "PasswordResetRequiredException") {
      // The error happens when the password is reset in the Cognito console
      // In this case you need to call forgotPassword to reset the password
      // Please check the Forgot Password part.
    } else if (err.code === "NotAuthorizedException") {
      // The error happens when the incorrect password is provided
    } else if (err.code === "UserNotFoundException") {
      // The error happens when the supplied username/email does not exist in the Cognito user pool
    }
    return false;
  } finally {
    Loading.hide();
  }
}

async function completeNewPassword(user, newPassword) {
  try {
    await Auth.completeNewPassword(
      user, // the Cognito User Object
      newPassword // the new password
    );
  } catch (error) {
    throw error;
  }

  const useur = await getUser(true);
  await apolloClient.mutate({
    mutation: ACEPTAR_CONDICIONES,
    variables: {
      id_paciente: useur.internal.id
    }
  });
  onLogin(apolloClient, useur.signInUserSession.accessToken.jwtToken).then(
    () => {
      AmplifyEventBus.$emit("authState", "signedIn");
    }
  );
}

function signOut() {
  return Auth.signOut()
    .then(async data => {
      await onLogout(apolloClient);
      AmplifyEventBus.$emit("authState", "signedOut");
      store.dispatch("auth/resetUser");
      return data;
    })
    .catch(err => {
      console.log(err);
      return err;
    });
}

async function changePassword(oldPassword, newPassword) {
  let currentUser = await Auth.currentAuthenticatedUser();
  if (currentUser) {
    await Auth.changePassword(currentUser, oldPassword, newPassword);
    let realUser = await getUser(true);
    store.dispatch("auth/setUser", { user: realUser });
  }
}

export {
  getUser,
  signUp,
  confirmSignUp,
  resendSignUp,
  signIn,
  signOut,
  completeNewPassword,
  changePassword
};
